import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import Resumable from "resumablejs";
import MediaHelper from "@Scripts/Common/MediaHelper";
import Localizer from "@Scripts/Common/Localizer";

@Component({ name: "Uploader" })
export default class Uploader extends Vue {
    // Reference: https://www.npmjs.com/package/resumablejs
    public localizer: any = Localizer;
    public resumable: any = null;
    public mode: string = null;
    public isUploadSupported: boolean = true;
    public progressStatus: any = null;
    public selectedFiles: any[] = [];
    public uploadedFiles: any[] = [];
    public uploadingErrors: any[] = [];

    @Prop() readonly uploadUrl: string
    @Prop() readonly uploadHeaders: any
    @Prop() readonly uploadParameters: any
    @Prop() readonly maxFileCount: number
    @Prop() readonly maxFileSize: number
    @Prop() readonly allowedFileExtensions: string[]

    public mounted() {
        this.$nextTick(() => {
            this.initResumable();
            this.setReadyMode();
        });
    }

    public initResumable() {
        const vue = this;

        this.resumable = new Resumable({
            target: this.uploadUrl,
            headers: this.uploadHeaders,
            query: this.uploadParameters,
            testChunks: false,
            simultaneousUploads: 1,
            chunkSize: 1 * 1024 * 1024,
            maxFiles: this.maxFileCount,
            chunkNumberParameterName: "chunkNumber",
            totalChunksParameterName: "chunkCount",
            chunkSizeParameterName: "chunkSize",
            totalSizeParameterName: "fileSize",
            fileNameParameterName: "fileName",
            fileType: this.allowedFileExtensions
        });

        if (this.resumable.support) {
            this.resumable.assignBrowse(this.$refs.browse);
            this.resumable.assignDrop(this.$refs.dropTarget);

            this.resumable.on("progress", function () {
                vue.progressStatus.loadedSize = vue.resumable.progress() * vue.resumable.getSize();
                vue.progressStatus.totalSize = vue.resumable.getSize();
                vue.progressStatus.percentage = Math.round(vue.progressStatus.loadedSize / vue.progressStatus.totalSize * 100);
                vue.$emit("progress", vue.progressStatus);
            });

            this.resumable.on("complete", function (file, message) {
                if (vue.uploadingErrors.length === 0) {
                    vue.$emit("uploadCompleted", vue.uploadedFiles);
                    vue.resumable.files = [];
                    vue.setReadyMode();
                    vue.setFileUploadedMode();
                } else {
                    vue.resumable.cancel();
                    vue.$emit("error", vue.uploadingErrors);
                    vue.cancelUpload();
                }
            });

            this.resumable.on("fileAdded", function (file, event) {
                vue.uploadingErrors = [];
                vue.setFileSelectedMode();
                vue.updateSelectedFiles();
                vue.$emit("fileAdded", { fileName: file.fileName, uniqueIdentifier: file.uniqueIdentifier });

                if (vue.maxFileCount === 1) {
                    // Auto upload file
                    this.upload();
                }
            });

            this.resumable.on("fileProgress", function (file) {
                if (!vue.resumable.opts.query.fileId) {
                    if (file.chunks.length !== 0) {
                        vue.resumable.opts.query.fileId = file.chunks[0].message();
                    }
                }
            });

            this.resumable.on("fileSuccess", function (file, message) {
                let uploadedFile = { fileId: vue.resumable.opts.query.fileId, fileName: file.fileName };
                vue.uploadedFiles.push(uploadedFile);

                vue.resumable.opts.query.fileId = null;
            });

            this.resumable.on("fileError", function (file, message) {
                let validationState = JSON.parse(message);
                if (validationState) {
                    // Set validation errors
                    for (var propertyName in validationState) {
                        if (validationState.hasOwnProperty(propertyName)) {
                            for (let message of validationState[propertyName]) {
                                vue.uploadingErrors.push({ message: file.fileName + ': ' + message });
                            }
                        }
                    }
                }
            });
        }
    }

    public setReadyMode() {
        this.mode = "Ready";
        this.uploadParameters.fileId = null;
        this.isUploadSupported = this.resumable.support;
        this.progressStatus = { loadedSize: 0, totalSize: 0, percentage: 0 };
        this.selectedFiles = [];
        this.updateSelectedFiles();
    }

    public setFileSelectedMode() {
        this.mode = "FileSelected";
    }

    public setUploadingMode() {
        this.mode = "Uploading";
    }

    public setFileUploadedMode() {
        this.mode = "FileUploaded";
    }

    public upload() {
        this.setUploadingMode();
        this.resumable.upload();
    }

    public cancelUpload() {
        this.resumable.cancel();
        this.uploadedFiles = [];
        this.setReadyMode();
        this.$emit("cancel");
    }

    public removeFile(selectedFile: any) {
        let files = this.resumable.files.filter(x => x.uniqueIdentifier === selectedFile.uniqueIdentifier);
        if (files.length !== 0) {
            let file = files[0];
            this.resumable.removeFile(file);
            this.updateSelectedFiles();

            if (this.selectedFiles.length === 0) {
                this.setReadyMode();
            }
        }
    }

    public updateSelectedFiles() {
        this.selectedFiles = [];
        for (let file of this.resumable.files) {
            this.selectedFiles.push({ fileName: file.fileName, uniqueIdentifier: file.uniqueIdentifier });
        }
    }

    public dragTargetEnter(e: any) {
        e.target.className = "droptarget active";
    }

    public dragTargetLeave(e: any) {
        e.target.className = "droptarget";
    }

    public dropOnTarget(e: any) {
        e.target.className = "droptarget";
    }

    public bytesToString(bytes: number) {
        return MediaHelper.bytesToString(bytes);
    }
};